import { AclService } from 'phoenix/services/aclService';
import {
	Acl, 
	AclPrincipal,
	AdvancedOption, 
	ObjectType, 
	RightsOption,
	RightsType,
	PermissionType,
	LinkSettings,
	ShareEntity,
	ShareInfo,
	ReceivedShareInfo,
	Permission,
	pxItemTypeGuards,
	ShareBaseItem,
	LinkCreationException,
	LinkCreationErrorCode
} from 'phoenix/domain/';
import {
	FolderDataStore
} from 'sharebase-app/dataStores/folderDataStore';
import {
	AclDatastore
} from 'sharebase/dataStores/aclDatastore';
import { CommonUtilsService } from 'util/services';
import { IDataStore } from 'sharebase/domain';
import {
	IInheritedShareableStore,
	IShareableStore,
	IShareableService,
	IShareServiceFactory,
	IShareService,
	ServerLinkShare,
	ClientCreatedLinkShare,
	ReceivedLinkShare,
	CreatedLinkShare,
	ClientLinkShare,
	RightsRestrictions,
	LinkShare,
	LinkCreationError
}  from 'sharebase/domain/sharebase-sharing';
import { AuthService, LinkCreationExceptionFactory } from 'phoenix/services';
import SettingsService from 'phoenix/services/settingsService';

export function ShareServiceFactory(
	$q:ng.IQService, 
	$http:ng.IHttpService, 
	$filter:ng.IFilterService, 
	utils:CommonUtilsService, 
	settingsService:SettingsService, 
	pxAuthService:AuthService,
	pxAclService:AclService, 
	pxLinkCreationExceptionFactory:LinkCreationExceptionFactory,
	sbAclDatastore:AclDatastore, 
	gettextCatalog
){
	let factory:IShareServiceFactory = {
		create: (service:IShareableService, store: IShareableStore) => {
			return new ShareService(service, store)
		}
	};

	class ShareService implements IShareService {
		private isInheritedStore:boolean = false;

		constructor(
			private shareableService:IShareableService,
			private store:IShareableStore
		) {
				this.isInheritedStore = isInheritedSharableStore(this.store);
		}

		public getShares(item:ShareBaseItem) {
			return sbAclDatastore
				.loadShares(item)
				.then(shares => this.assignShares(shares));
		}

		public getLinks(item:ShareBaseItem) {
			return this.shareableService
				.getLinks(item)
				.then(links => links
					.filter(link => !link.IsExpired && link.Sender === pxAuthService.displayName)
					.map(prinicpal => this.elaborateSharedLink(item, prinicpal))
				)
				.then(links => this.assignLinks(links));
		}

		public createShare(item:ShareBaseItem, entity:ShareEntity, right:RightsType, advanced:AdvancedOption[]): ng.IPromise<Acl[]> {
			// coerce single entity into array to proxy to createShares()
			var entities = [entity];
			return this.createShares(item, entities, right, advanced);
		}

		public createLink(item:ShareBaseItem, share:ServerLinkShare, right:RightsOption) {
			return this.getCurrentUserAcl(item)
				.then(userAcl => {
					share.Embedded.Permissions = pxAclService
						.create(0, this.store.type, 'anonymous', false)
						.setRights(right.id, right.embedded.options)
						.apply(userAcl)
						.toServer()
						.Embedded
						.Permissions;
					return this.shareableService.createLink(item, share);
				})
				.catch((e:ng.IHttpResponse<any>) => {
					if (e.data.InvalidPassword){
						throw pxLinkCreationExceptionFactory.create(LinkCreationErrorCode.PASSWORD_POLICY, e.data.Error);
					} else {
						throw pxLinkCreationExceptionFactory.create(LinkCreationErrorCode.DEFAULT)
					}
				})
				.then(link => this.elaborateSharedLink(item, link) as ClientCreatedLinkShare)
				.then(link => this.assignLink(link) as ClientCreatedLinkShare);
		}

		private getCurrentItem() {
			let store:any = this.store as any;
			return (store.folder || store.document || store.library) as ShareBaseItem;
		}

		private elaborateSharedLink(item:ShareBaseItem, link: ReceivedLinkShare | CreatedLinkShare) {
			let clientLink:ClientLinkShare = angular.copy(link) as any;
			let acl = pxAclService.create(link as any);
			let exp = new Date(clientLink.DateExpires.toString());
			let itemType = ObjectType[this.store.type].toLowerCase();
			let id = pxItemTypeGuards.isShareBaseDocument(item) ? item.DocumentId :
				pxItemTypeGuards.isShareBaseFolder(item) ? item.FolderId :
				item.LibraryId;

			// generate a unique client ID to remove shares from the datastore
			clientLink.ClientId = 'link-' + Date.now() + (isReceivedLinkShare(link) ? link.Id : 0).toString(); 
			clientLink.DeleteExpanded = false;			
			clientLink.LinkUrl = `${settingsService.serverWebPath}/${itemType}/${id}/share/${link.ReferenceString}`;
			clientLink.IsInherited = false;
			clientLink.IsRevoked = false;

			// all links created on this item are not inherited by definition
			if(isReceivedLinkShare(link)){
				switch(this.store.type) {
					// any folder link must be inherited if the current item is a document
					case ObjectType.DOCUMENT:
						clientLink.IsInherited = link.ObjectType === 'Folder';
						break;
					// if a folder, any folder link with a different name from this item must be inherited 
					case ObjectType.FOLDER:
						let store = this.store as FolderDataStore;
						clientLink.IsInherited = link.ObjectType === 'Folder' && 
							store.folder &&
							link.ObjectName !== store.folder.FolderName; 
						break;
					default:
						break;
				}
			}
			
			if (exp.getFullYear() === 9999) {
                clientLink.ExpireString = gettextCatalog.getString('Never');
			}
			else {
                clientLink.ExpireString = $filter('date')(exp, 'mediumDate');
			}

            if (clientLink.IsExpired) {
                clientLink.IsRevoked = exp.getTime() > Date.now();
            }
            
			clientLink.RightString = (rightName => {
				var pl = '';

				if (acl.hasRight(RightsType.VIEW)) {
					if (acl.hasAllPermissions(PermissionType.ADDDOC + PermissionType.DOWNLOAD))
						pl = ' + ' + gettextCatalog.getString('Up/Download');
					else if (acl.hasPermission(PermissionType.ADDDOC))
						pl = ' + ' + gettextCatalog.getString('Upload');
					else if (acl.hasPermission(PermissionType.DOWNLOAD))
						pl = ' + ' + gettextCatalog.getString('Download');
				}
				else if (acl.hasRight(RightsType.EDIT) && acl.hasPermission(PermissionType.DELETE)) {
					pl = ' + ' + gettextCatalog.getString('Delete');
				}
				else if (acl.hasRight(RightsType.UPLOADONLY) && acl.hasPermission(PermissionType.LISTFOLD)) {
					pl = ' + ' + gettextCatalog.getString('List');
				}

				return rightName + pl;
			})(acl.rightName);

			return clientLink;
        }

		public revokeLink(revokedLink:ClientLinkShare) {
			return this.shareableService
				.revokeLink(revokedLink)
				.then(result => {
					return this.store.links =  this.store.links.filter(link => link.ClientId !== revokedLink.ClientId);					
				});
		}

		public createShares(item:ShareBaseItem, entities:ShareEntity[], right:RightsType, advanced:AdvancedOption[]): ng.IPromise<Acl[]> {
			let existingShares:Acl[] = this.store.shares;
			let newShares = entities
				.map(entity => {
					return pxAclService
						.generateForEntity(entity, this.store.type)
						.setRights(right, advanced);				
				});
			
			if(isInheritedSharableStore(this.store)){
				existingShares = existingShares.concat(this.store.inheritedShares);
			}

			let allShares = existingShares
				// ensure no duplicates between new and existing shares
				.filter(existingShare => {
					// prune out existing shares; 
					// not ideal since it's O(n*x) but working with a dict isn't convenient and sets are small
					var isDuplicate = false;
					var newShare:Acl;
					for(var i = 0; i < newShares.length; i++) {
						newShare = newShares[i];
						
						if(existingShare.elementId === newShare.elementId) {
							isDuplicate = true;
							break;
						}
					}

					return !isDuplicate;
				})
				.concat(newShares);

			// coerce non-inherited domain objects to data access format
			var serverRequests = newShares
				.filter(share => !share.isInherited)
				.map(share => share.toServer())
				.map(share => this.shareableService.createShare(item, share));

			return $q.all(serverRequests)
				.then(() => {
					// TODO: When SP-473 is implemented, reinstate the commented code below
					// sbAclDatastore.addToSharesCaches(item, newShares);
					// return this.assignShares(allShares)
					sbAclDatastore.clearShareCache(item);
					return sbAclDatastore.loadShares(item)
						.then(res => this.assignShares(res));
				});
		}

		public updateShare(item:ShareBaseItem, updatedShare:Acl, right:RightsType, advanced:AdvancedOption[]): ng.IPromise<Acl> {			
			let filterPredicate = (share:Acl) => share.elementId !== updatedShare.elementId;
			let updatedShares:Acl[] = angular.copy(this.store.shares).filter(filterPredicate);
			let isInherited = updatedShare.isInherited;

			updatedShare.setRights(right, advanced)
			if(isInherited){
				updatedShare.isInherited = false;
			}

			updatedShares.push(updatedShare);

			let p:ng.IPromise<ng.IHttpResponse<any>>;
			let serverShape = updatedShare.toServer();

			// this is an inherited/virtual ACL, so we need to create a new explicit one (e.g., revoke access)
			if(isInherited){
				p = this.shareableService
					.createShare(item, serverShape)
			} else {
				p = this.shareableService
					.updateShare(item, serverShape)
			}			

			return p.then(() =>  {
				let p:ng.IPromise<any> = $q.resolve();
				if(isInherited){
					// TODO: remove when SP-473 is implemented
					// let iStore = this.store as IInheritedShareableStore;
					// iStore.inheritedShares = iStore.inheritedShares.filter(filterPredicate);
					sbAclDatastore.clearShareCache(item);			
					p = sbAclDatastore
						.loadShares(item)
						.then(res => this.assignShares(res));		
				}
				else {
					this.store.shares = updatedShares;
				}

				return p.then(() => updatedShare);
			});
		}


		public deleteShare(item:ShareBaseItem, deletedShare:Acl): ng.IPromise<Acl[]> {
			// modify local share collection in anticipation of success; otherwise discard
			var remainingShares:Acl[] = this.store.shares.filter(share => share.elementId !== deletedShare.elementId);

			if(isInheritedSharableStore(this.store)){
				remainingShares = remainingShares.concat(this.store.inheritedShares);
			}
			
			return this.shareableService
				.deleteShare(item, deletedShare.toServer())
				.then(() => {
					sbAclDatastore.removeFromSharesCaches(item, deletedShare);
					return this.assignShares(remainingShares);
				});
		}

		public getCurrentUserAcl(item:ShareBaseItem): ng.IPromise<Acl> {
			if(angular.isNumber(item)){
				let itemId = +item;
				return this.shareableService
					.getPermissionsForCurrentUser(item)
					.then(perms => pxAclService.create(perms[0], this.store.type));
			}
			else {
				return sbAclDatastore.load(item as ShareBaseItem);
			}
		}

		public getRights(sharer:Acl, share:Acl, isInherited:boolean = false): ng.IPromise<RightsRestrictions> {
			var p = $q.resolve(sharer as Acl);

			return p.then(sharerAcl => {
				return this.shareableService
					.getRights(sharerAcl.allowPerms, share ? share.allowPerms : 0, share ? share.isInherited : isInherited)
					.then(rr => utils.propsToCamel(rr));
			});
		}

		public getLinkRights(sharer:Acl) {
			if(!sharer) {
				throw new Error('ACL required.');
			}

			return this.shareableService
				.getLinkRights(sharer.allowPerms)
				.then(rr => utils.propsToCamel(rr))
				.then(rr => {
					// guarantee rights are sorted from least permissive 
					// to most so that views can default to least
					rr.rights.sort((r1, r2) => {
						if(r1.mask === r2.mask){
							return 0
						}
						else {
							return r1.mask > r2.mask ? 1 : -1;
						}
					});
					return rr;
				});
							
		}
	

		public sendShareMessage(item:ShareBaseItem, subject:string, message:string): ng.IHttpPromise<any>{		
			return $http.post(item.Links.MailSharedUsersUri, {Body: message, Subject: subject});
		}


		private assignShares(shares:Acl[]){			
			this.store.shares = [];
			if(this.isInheritedStore) {
				(this.store as IInheritedShareableStore).inheritedShares= [];
			}

			shares.forEach(acl => {
				var bucket:Acl[] = acl.isInherited && this.isInheritedStore ?
					(this.store as IInheritedShareableStore).inheritedShares :
					this.store.shares;					

				bucket.push(acl);
			});
			return shares;
		}

		private assignLinks(links:ClientLinkShare[]){
			this.store.links = [];
			if(this.isInheritedStore){
				(this.store as IInheritedShareableStore).inheritedLinks = [];
			}

			links.forEach(link => this.assignLink(link));
			return links;
		}

		private assignLink(link:ClientLinkShare) {
			let links = link.IsInherited && this.isInheritedStore ? 
				(this.store as IInheritedShareableStore).inheritedLinks : 
				this.store.links;

			links.push(link);
			return link;
		}
		
		private toShare(principal:AclPrincipal): Acl {
			return pxAclService.create(principal);
		}

		private toServerJson(acls:Acl[]): ShareInfo[] {
			// coerce domain objects to data access format
			return acls.map(share => share.toServer());
		}
	}

	function isReceivedLinkShare(share:LinkShare): share is ReceivedLinkShare {
		return angular.isDefined((share as ReceivedLinkShare).Id) &&
			angular.isDefined((share as ReceivedLinkShare).ObjectId);
	}

	function isInheritedSharableStore(share:IShareableStore): share is IInheritedShareableStore {
		return angular.isDefined((share as IInheritedShareableStore).inheritedShares);
	}

	function isLinkCreationError(share): share is LinkCreationError {
		return angular.isDefined((share as LinkCreationError).Error);
	}

	return factory;
};
ShareServiceFactory.$inject = [
	'$q', 
	'$http', 
	'$filter',
	'utils',
	'settingsService',
	'pxAuthService',
	'pxAclService',  
	'pxLinkCreationExceptionFactory',
	'sbAclDatastore',
	'gettextCatalog'
];


// custom, semantic error class adds no new functionality
class ShareServiceConfigError extends Error {
	public name:string = 'ShareServiceConfigError';
	public stack:string;
	
	constructor(public message:string) {
		super(message);
		this.stack = (<any>new Error()).stack;
	}

	toString(){
		return this.name + ': ' + this.message;
	}
}




// WEBPACK FOOTER //
// ./src/sharebase/factories/shareService.ts